import {
  Component, Input, OnChanges, SimpleChanges, OnDestroy
} from '@angular/core';
import { Router } from '@angular/router';
import {
  Firestore, collection, query, where, getDocs, Timestamp
} from '@angular/fire/firestore';
import { SubscriptionPlanService, AdminPlan } from
  'src/app/subscription-management/subscription-services/subscription-plan.service';
import { Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';

export interface SidebarPlanInfo {
  name: string;
  tagline: string;
  color: string;
  marketplaceEnabled: boolean;
  activeListingLimit: number;
  activeListings: number;
  renewalDate: Date | null;
  listingPercent: number;
  bookingLimit: number;
  storageLimitGb: number;
  listingFee: number;
  addOnBookingEnabled: boolean;
  monthlyFee: number;
  term: string;
  isFree: boolean;
}

export interface UsageData {
  bookingsUsed: number;
  bookingsRemaining: number;
  daysUntilRenewal: number;
  totalVehicles: number;
  storageUsedGb: number;
}

@Component({
  selector: 'app-subscription-summary-card',
  templateUrl: './subscription-summary-card.component.html',
  styleUrls: ['./subscription-summary-card.component.scss'],
})
export class SubscriptionSummaryCardComponent implements OnChanges, OnDestroy {
  @Input() profile: any = null;

  sidebarPlan: SidebarPlanInfo | null = null;
  planLoading = true;
  modalOpen = false;
  allPlans: AdminPlan[] = [];
  currentPlanId: string = '';

  usageData: UsageData = {
    bookingsUsed: 0,
    bookingsRemaining: 0,
    daysUntilRenewal: 0,
    totalVehicles: 0,
    storageUsedGb: 0,
  };

  private planSub?: Subscription;

  constructor(
    private planService: SubscriptionPlanService,
    private firestore: Firestore,
    private router: Router,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['profile'] && this.profile?.plan) {
      this.loadPlanData(this.profile);
    }
  }

  ngOnDestroy(): void {
    this.planSub?.unsubscribe();
  }

  loadPlanData(profile: any): void {
    this.planLoading = true;
    this.currentPlanId = profile.plan;

    this.planSub = this.planService.plans$
      .pipe(filter(p => p.length > 0), first())
      .subscribe(async (plans) => {
        this.allPlans = plans;
        const matched = plans.find(p => p.id === profile.plan);
        if (!matched) { this.planLoading = false; return; }

        const renewalDate = profile.subscriptionExpiry
          ? new Date(profile.subscriptionExpiry) : null;

        const activeListings = matched.marketplaceEnabled
          ? await this.countActiveListings(profile.uid ?? profile.id) : 0;

        const limit = matched.activeListingLimit ?? 0;
        const listingPercent = limit > 0
          ? Math.min(100, Math.round((activeListings / limit) * 100)) : 0;

        this.sidebarPlan = {
          name: matched.name,
          tagline: matched.tagline ?? '',
          color: matched.color ?? '#0ea5e9',
          marketplaceEnabled: matched.marketplaceEnabled ?? false,
          activeListingLimit: limit,
          activeListings,
          renewalDate,
          listingPercent,
          bookingLimit: matched.bookingLimit ?? 0,
          storageLimitGb: matched.storageLimitGb ?? 0,
          listingFee: matched.listingFee ?? 0,
          addOnBookingEnabled: matched.addOnBookingEnabled ?? false,
          monthlyFee: matched.monthlyFee ?? 0,
          term: profile.term ?? 'monthly',
          isFree: matched.monthlyFee === 0,
        };

        // build usage
        const bookingsUsed = profile.manualBookingsUsed ?? 0;
        const bookingLimit = matched.bookingLimit === 0 ? 999 : matched.bookingLimit;
        const daysUntilRenewal = renewalDate
          ? Math.max(0, Math.ceil((renewalDate.getTime() - Date.now()) / 86400000)) : 0;

        this.usageData = {
          bookingsUsed,
          bookingsRemaining: Math.max(0, bookingLimit - bookingsUsed),
          daysUntilRenewal,
          totalVehicles: await this.countTotalVehicles(profile.uid ?? profile.id),
          storageUsedGb: 0,
        };

        this.planLoading = false;
      });
  }

  async countActiveListings(uid: string): Promise<number> {
    if (!uid) return 0;
    try {
      const q = query(
        collection(this.firestore, 'listings'),
        where('vendorId', '==', uid),
        where('isMarketplaceActive', '==', true),
      );
      const snap = await getDocs(q);
      return snap.docs.filter(d => d.data()['isMarketplaceActive'] === true).length;
    } catch { return 0; }
  }

  async countTotalVehicles(uid: string): Promise<number> {
    if (!uid) return 0;
    try {
      const q = query(
        collection(this.firestore, 'equipment-list'),
        where('bywhom', '==', uid),
      );
      return (await getDocs(q)).size;
    } catch { return 0; }
  }

  openModal(): void { this.modalOpen = true; }
  closeModal(): void { this.modalOpen = false; }

  navigateToUpgrade(): void {
    this.closeModal();
    this.router.navigate(['/business/subscription']);
  }

  navigateToInvoices(): void {
    this.closeModal();
    this.router.navigate(['/business/invoices']);
  }
}